// Sharing a Khronos Timer with mates (#171). The owner shares; a mate can then
// send their own sessions to it, and nothing else. First in keeps the timer:
// while a session is on it, anyone else sending is told who has it. The rules
// live in api/lib/deviceShares.js; nothing here is trusted for them.

import React, { useCallback, useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import api from "../../services/api";
import { useSocial } from "../../contexts/SocialContext";
import { useToast } from "../../contexts/ToastContext";
import { cn } from "../../lib/utils";
import { EXPIRY_CHOICES, expiryFromChoice, firstName } from "../../utils/creditShares";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { DeleteConfirm, Field, OptionList } from "../profile/ProfileParts";
import { Avatar, SheetGroup } from "./creditParts";
import { formatLastSeen } from "./settingsSummaries";

const untilLabel = (iso) =>
  iso
    ? `Until ${new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`
    : "No end date";

/** Who the owner has shared a timer with, and the calls to change it. */
export const useTimerShares = (deviceId) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (deviceId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .getDeviceShares(deviceId)
      .then((rows) => !cancelled && setShares(rows || []))
      .catch(() => !cancelled && setShares([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  const share = useCallback(
    async (userId, expiresAt) => setShares((await api.shareDevice(deviceId, userId, expiresAt)) || []),
    [deviceId],
  );
  const stop = useCallback(async (shareId) => {
    await api.stopDeviceShare(shareId);
    setShares((prev) => prev.filter((s) => s.id !== shareId));
  }, []);

  return { shares, loading, share, stop };
};

/** The "Shared with" block on the owner's timer sheet. */
export const TimerShares = ({ sharing, onAdd }) => {
  const { showToast } = useToast();
  const [stopping, setStopping] = useState(null);

  const stop = async (s) => {
    setStopping(s.id);
    try {
      await sharing.stop(s.id);
    } catch (err) {
      showToast({ title: "Couldn't stop sharing", description: err.message });
    } finally {
      setStopping(null);
    }
  };

  return (
    <Field
      label="Shared with"
      hint="Mates you share it with can send their own sessions to it. Whoever starts first keeps it until their session ends."
    >
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
        {sharing.loading && sharing.shares.length === 0 ? (
          <p className="px-3 py-3 text-sm text-muted-foreground">Loading…</p>
        ) : (
          sharing.shares.map((s) => (
            <div key={s.id} className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5">
              <Avatar name={s.user_name} />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">{s.user_name}</span>
                <span className="block truncate text-xs text-muted-foreground">{untilLabel(s.expires_at)}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Stop sharing with ${s.user_name}`}
                onClick={() => stop(s)}
                disabled={stopping === s.id}
              >
                {stopping === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
          ))
        )}
        <button
          type="button"
          onClick={onAdd}
          className="w-full px-3 py-3 text-left text-sm font-semibold text-primary transition-colors hover:bg-muted/30"
        >
          Share with a mate
        </button>
      </div>
    </Field>
  );
};

/** Pick a mate and an end date, then share. A view inside the timer sheet. */
export const ShareTimerView = ({ device, sharing, onDone }) => {
  const { friends = [] } = useSocial() || {};
  const { showToast } = useToast();
  const [mateId, setMateId] = useState(null);
  const [ends, setEnds] = useState("none");
  const [saving, setSaving] = useState(false);
  const sharingWith = new Set(sharing.shares.map((s) => s.user_id));

  useEffect(() => {
    setMateId(friends.length === 1 ? friends[0].id : null);
    setEnds("none");
    // Fresh choices each time the view opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!mateId || saving) return;
    setSaving(true);
    try {
      await sharing.share(mateId, expiryFromChoice(ends));
      const mate = friends.find((f) => f.id === mateId);
      showToast({ title: `Shared ${device.name} with ${firstName(mate?.name)}` });
      onDone();
    } catch (err) {
      showToast({ title: "Couldn't share the timer", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (friends.length === 0) {
    return (
      <p className="rounded-xl border border-border bg-background p-3 text-sm text-muted-foreground">
        You can share a timer with your mates. Add some in Social › Mates first.
      </p>
    );
  }

  return (
    <>
      <SheetGroup title="Mate">
        {friends.map((friend) => {
          const selected = mateId === friend.id;
          return (
            <button
              key={friend.id}
              type="button"
              aria-pressed={selected}
              onClick={() => setMateId(friend.id)}
              className={cn(
                "grid w-full grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 text-left transition-colors",
                selected ? "bg-primary/10" : "hover:bg-muted/30",
              )}
            >
              <Avatar name={friend.name} />
              <span className={cn("truncate text-sm font-semibold", selected && "text-primary")}>{friend.name}</span>
              {sharingWith.has(friend.id) && <span className="text-xs text-muted-foreground">Change end date</span>}
            </button>
          );
        })}
      </SheetGroup>
      <Field label="Ends">
        <OptionList
          options={EXPIRY_CHOICES.map((c) => ({
            value: c.value,
            label: c.label,
            description: c.value === "none" ? "Until you stop it" : untilLabel(expiryFromChoice(c.value)),
          }))}
          value={ends}
          onChange={setEnds}
        />
      </Field>
      <Button type="button" className="h-12 w-full text-base font-semibold" onClick={submit} disabled={!mateId || saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Share
      </Button>
    </>
  );
};

/** A timer a mate shared with you: whose it is, who's on it, and give it back. */
export const SharedTimerSheet = ({ deviceId, timers, onClose }) => {
  const { showToast } = useToast();
  const live = timers.shared.find((d) => d.id === deviceId) || null;
  const [shown, setShown] = useState(live);
  useEffect(() => {
    if (live) setShown(live);
  }, [live]);
  const device = live || shown;
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => setConfirming(false), [deviceId]);

  if (!device) return null;
  const online = timers.online[device.id] === true;
  const owner = firstName(device.owner_name);

  const leave = async () => {
    setLeaving(true);
    try {
      await timers.leave(device);
      showToast({ title: `Gave ${device.name} back to ${owner}` });
      onClose();
    } catch (err) {
      showToast({ title: "Couldn't give the timer back", description: err.message });
    } finally {
      setLeaving(false);
      setConfirming(false);
    }
  };

  return (
    <DetailSheet open={deviceId != null && !!live} onOpenChange={(o) => !o && onClose()} title={device.name}>
      <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 text-sm">
        <span className={`h-2 w-2 shrink-0 rounded-full ${online ? "bg-green-500" : "bg-muted-foreground/60"}`} />
        <span className="font-semibold">
          {device.in_use_by ? `In use by ${firstName(device.in_use_by)}` : online ? "Online" : "Offline"}
        </span>
        {!online && !device.in_use_by && (
          <span className="truncate text-muted-foreground">
            · last seen {formatLastSeen(device.last_seen_at).toLowerCase()}
          </span>
        )}
      </div>
      <SheetGroup>
        <div className="grid gap-0.5 px-3 py-3 text-sm">
          <span className="font-semibold">{device.owner_name} shared it with you</span>
          <span className="text-xs text-muted-foreground">{untilLabel(device.expires_at)}</span>
        </div>
      </SheetGroup>
      <p className="text-[13px] leading-snug text-muted-foreground">
        Send a session to it from the Coach before you start. Whoever starts first keeps it until their session ends.
        Its name, settings and updates are {owner}&rsquo;s to change.
      </p>
      <DeleteConfirm
        action="Give back"
        name={device.name}
        consequence={`It leaves your timers. ${owner} can share it again.`}
        confirming={confirming}
        onAsk={() => setConfirming(true)}
        onCancel={() => setConfirming(false)}
        onConfirm={leave}
        busy={leaving}
      />
    </DetailSheet>
  );
};
